/**
 * TG-6 (AC-14): combines TG-6a's `SectionInstance` list with the owner's
 * assignment read and the confirmed-answers map into the five H2 status
 * markers, one row per section INSTANCE (two Commercial property policies
 * are two rows, each with its own policy number).
 *
 * Delegation grain is the section instance (ADR-20261015-h2s6): the
 * assignment `sectionId` TG-2's routes store is the instance's
 * `assignmentKeyFor` value, so ticking one policy's row never assigns the
 * other. An additional-details page belongs to the instance that inserted
 * it and inherits that instance's assignment.
 */
import type { ContributorSectionState, SectionAssignmentStatus } from "../../portal-auth";
import { decodeSectionInstanceId, encodeSectionInstanceId } from "../../routing/route-table";
import type { ConfirmedAnswers, SectionInstanceId } from "../../routing/route-table";
import type { SectionStateEntry } from "../../routing/types";
import type { SectionInstance } from "../../state/section-instances";
import { getSectionLabel } from "./content";

export type SectionStatus = "to-do" | "in-progress" | "completed" | "assigned" | "completed-by-colleague";

export interface SectionListRow {
  readonly instanceId: SectionInstanceId;
  readonly assignmentKey: SectionInstanceId;
  readonly order: number;
  readonly title: string;
  readonly policyNumber?: string;
  /** ADR-20261027-81ff: the DISPLAY-only policy number -- present for
   * every policy-specific section (one with a `lineOfBusinessCode`),
   * whether or not its type repeats on this renewal. Render this, never
   * `policyNumber` above, anywhere the row names itself to the customer;
   * `policyNumber` stays reserved for id/key logic (`assignmentKeyFor`
   * and friends), which must never see this field. */
  readonly displayPolicyNumber?: string;
  readonly isAdditionalDetailsPage: boolean;
  readonly status: SectionStatus;
  readonly assigneeName?: string;
  readonly assigneeEmail?: string;
  readonly assigneeSectionAssigneeId?: number;
}

/** The id an assignment for `instanceId` is stored under: the instance
 * itself with any additional-details page key removed. */
export function assignmentKeyFor(instanceId: SectionInstanceId): SectionInstanceId {
  const { sectionId, policyNumber } = decodeSectionInstanceId(instanceId);
  return encodeSectionInstanceId({ sectionId, ...(policyNumber !== undefined ? { policyNumber } : {}) });
}

/**
 * OBJ-R-8/FIND-849 (2026-10-19): `confirmedAnswers` alone is NOT the
 * completion signal for a contributor's OWN row. The working-copy write
 * (`confirmSection`) that populates `confirmedAnswers` runs before the
 * completion marker PUT (`putContributorSectionState`) in
 * `ExposuresJourneyRoute.handleContinue`, and that marker -- not the
 * working copy -- is what `dbo.section_assignment.state` and Submit's
 * 422 `incompleteSectionIds` check actually read. A dropped/failed
 * marker PUT therefore leaves `confirmedAnswers` populated while the
 * server still has the row as not-done; a page reload rebuilds
 * `confirmedAnswers` from the (already-persisted) working copy and, with
 * no reconciliation, shows "Completed" for a section the server still
 * refuses at Submit. `contributorState`, when supplied, is the
 * `GET /api/portal/sections/{id}` read the caller fetches for every
 * delegated row on mount (and updates optimistically on a successful
 * marker PUT) and takes precedence over `confirmedAnswers` for exactly
 * this reason. It is `undefined` for an owner's own rows (no marker
 * concept applies) and, transiently, before the contributor fetch
 * settles -- in both cases this falls back to the prior
 * `confirmedAnswers`-only behaviour.
 */
function statusFor(
  instance: SectionInstance,
  confirmedAnswers: ConfirmedAnswers,
  assignment: SectionAssignmentStatus | undefined,
  inProgressInstanceIds: ReadonlySet<string>,
  contributorState: ContributorSectionState | undefined,
): SectionStatus {
  if (assignment !== undefined) {
    return assignment.state === "completed_by_colleague" ? "completed-by-colleague" : "assigned";
  }
  if (contributorState !== undefined && contributorState !== "completed") {
    if (contributorState === "in_progress" || confirmedAnswers[instance.instanceId] !== undefined) {
      return "in-progress";
    }
    return unconfirmedStatusFor(instance, inProgressInstanceIds);
  }
  // OBJ-C-15/OBJ-R-9 (FIND-856/FIND-858, 2026-10-19): `contributorState ===
  // "completed"` must never promote this instance to "completed" on its
  // own. The server marker is keyed by `assignmentKeyFor`, which strips an
  // additional-details page's own key down to its parent's -- so the
  // parent's marker going "completed" (PUT at the parent's Continue) was
  // reaching every details page that shares the key, including one the
  // colleague has never opened. The marker may only DEMOTE a
  // `confirmedAnswers`-derived "completed" (handled above, before this
  // point is reached); it may never PROMOTE an instance with no
  // `confirmedAnswers` entry of its own.
  if (confirmedAnswers[instance.instanceId] !== undefined) {
    return "completed";
  }
  return unconfirmedStatusFor(instance, inProgressInstanceIds);
}

/** An instance with no confirmed answers of its own: "in-progress" when
 * `inProgressInstanceIds` names it, otherwise "to-do". */
function unconfirmedStatusFor(instance: SectionInstance, inProgressInstanceIds: ReadonlySet<string>): SectionStatus {
  return inProgressInstanceIds.has(instance.instanceId) ? "in-progress" : "to-do";
}

export function buildSectionListRows(
  instances: readonly SectionInstance[],
  confirmedAnswers: ConfirmedAnswers,
  assignments: readonly SectionAssignmentStatus[],
  inProgressInstanceIds: ReadonlySet<string> = new Set(),
  contributorStates: ReadonlyMap<string, ContributorSectionState> = new Map(),
): readonly SectionListRow[] {
  const assignmentByKey = new Map(assignments.map((assignment) => [assignment.sectionId, assignment]));

  return instances.map((instance) => {
    const assignmentKey = assignmentKeyFor(instance.instanceId);
    const assignment = assignmentByKey.get(assignmentKey);
    return {
      instanceId: instance.instanceId,
      assignmentKey,
      order: instance.order,
      title: getSectionLabel(instance.labelKey),
      isAdditionalDetailsPage: instance.additionalDetailsPageKey !== undefined,
      status: statusFor(instance, confirmedAnswers, assignment, inProgressInstanceIds, contributorStates.get(assignmentKey)),
      ...(instance.policyNumber !== undefined ? { policyNumber: instance.policyNumber } : {}),
      ...(instance.displayPolicyNumber !== undefined ? { displayPolicyNumber: instance.displayPolicyNumber } : {}),
      ...(assignment !== undefined
        ? {
            assigneeName: assignment.assigneeName,
            assigneeEmail: assignment.assigneeEmail,
            assigneeSectionAssigneeId: assignment.sectionAssigneeId,
          }
        : {}),
    };
  });
}

/** AC-32: "Continue and submit" appears only once every row is Completed
 * or Completed by a colleague. */
export function everyRowSettled(rows: readonly SectionListRow[]): boolean {
  return rows.every((row) => row.status === "completed" || row.status === "completed-by-colleague");
}

/**
 * REQ-001 (AC-3): the step guard's view of each exposures-hosted section,
 * derived from the SAME rows the owner's section list renders
 * (`buildSectionListRows` over the same instances, confirmed answers and
 * assignments), so the guard can never admit the declaration while the
 * list still shows a row as anything but "Completed" or "Completed by a
 * colleague". A section is settled exactly when every one of its rows is
 * settled. A live assignment decides its rows (`statusFor`), so a
 * colleague's confirmed page does not settle a section they have not
 * submitted. Entries hosted elsewhere, "inapplicable" entries and entries
 * with no rows are returned unchanged.
 */
export function applySectionListStatus(
  sectionStates: readonly SectionStateEntry[],
  instances: readonly SectionInstance[],
  confirmedAnswers: ConfirmedAnswers,
  assignments: readonly SectionAssignmentStatus[],
): readonly SectionStateEntry[] {
  const rowsBySectionId = new Map<string, SectionListRow[]>();
  for (const row of buildSectionListRows(instances, confirmedAnswers, assignments)) {
    const { sectionId } = decodeSectionInstanceId(row.instanceId);
    rowsBySectionId.set(sectionId, [...(rowsBySectionId.get(sectionId) ?? []), row]);
  }
  return sectionStates.map((entry) => {
    const sectionRows = rowsBySectionId.get(entry.sectionId);
    if (entry.state === "inapplicable" || sectionRows === undefined) {
      return entry;
    }
    if (everyRowSettled(sectionRows)) {
      return { ...entry, state: "confirmed" as const };
    }
    return entry.state === "confirmed" ? { ...entry, state: "open" as const } : entry;
  });
}

/** AC-14: the "You've completed <n> of <m> sections." count. */
export function completedRowCount(rows: readonly SectionListRow[]): number {
  return rows.filter((row) => row.status === "completed" || row.status === "completed-by-colleague").length;
}

export function sectionTitleId(row: SectionListRow): string {
  return `section-list-title-${row.instanceId}`;
}

export function tickId(row: SectionListRow): string {
  return `section-list-tick-${row.instanceId}`;
}

/** The first row's checkbox id: where AC-16's no-tick error links to. */
export function firstTickId(rows: readonly SectionListRow[]): string {
  const first = rows.find((row) => !row.isAdditionalDetailsPage);
  return first !== undefined ? tickId(first) : "";
}
